/**
 * Zustand store with `zundo` temporal middleware (FEAT-2026-0005, Phase 2).
 *
 * Holds the WORKING DRAFT of a template config — the value the user is
 * actively editing in the form / WYSIWYG / JSON tab. Three-mode editing
 * means every mode writes here, so undo/redo (Cmd+Z / Cmd+Shift+Z) works
 * across the whole editor — drag a label, change a number, edit the JSON,
 * then undo through every step.
 *
 * `temporal` middleware tracks past/future snapshots; `partialize` excludes
 * non-config UI state (selected tab, dialog open, …) from the history so a
 * tab switch doesn't pollute Cmd+Z.
 */

import { create } from 'zustand';
import { temporal } from 'zundo';
import type { TemporalState } from 'zundo';

interface ReportTemplateState {
  /** The active template (selected in the left rail). */
  templateKey: string | null;
  setTemplateKey: (key: string | null) => void;

  /** Full config JSON object, edited through any of the three panes. */
  draftConfig: unknown;
  setDraftConfig: (next: unknown) => void;
  /** Replace AND mark "saved" (e.g. after server returns the new version). */
  resetDraftConfig: (next: unknown) => void;

  /** Last published config (from the server). Used to compute the dirty flag. */
  publishedConfig: unknown;
  setPublishedConfig: (next: unknown) => void;

  /** Selected sample for the live preview pane. */
  previewWorkOrderDetailKey: string | null;
  setPreviewWorkOrderDetailKey: (key: string | null) => void;

  /**
   * Name of the form section the user is currently focused on (e.g.
   * "page", "trialTables", "flowCurve"). Drives the highlight overlay
   * on the live PDF preview so the user can SEE which region of the
   * report the field they're editing affects. `null` = no highlight.
   *
   * Excluded from the undo history (via `partialize`) since it's pure
   * UI state, not config data.
   */
  activeSection: string | null;
  setActiveSection: (section: string | null) => void;

  /**
   * Latest rendered preview PDF as an `URL.createObjectURL` blob handle.
   * Held in the store (rather than local component state) so the same
   * blob can outlive PreviewPane re-mounts. The setter owns the
   * `URL.revokeObjectURL` lifecycle: the previous URL is revoked when
   * a new one is set or when null is written, so callers never have to
   * remember to clean up.
   */
  previewPdfBlobUrl: string | null;
  setPreviewPdfBlobUrl: (url: string | null) => void;

  /** True while the most recent preview render is in flight. */
  previewPdfLoading: boolean;
  setPreviewPdfLoading: (loading: boolean) => void;

  /** Last preview-render error message (null when the last render succeeded). */
  previewPdfError: string | null;
  setPreviewPdfError: (err: string | null) => void;

  /**
   * Visual canvas zoom level in display-pixel-per-PDF-point units (Phase 5).
   * Default 0.7 — the page mock fits a typical settings pane comfortably
   * at this scale, matching the pre-zoom-feature behaviour. Clamped
   * to [0.4, 2.5] inside `setCanvasScale` (mirrors PreviewPane). Pure
   * editor preference, NOT a per-template setting; lives outside the
   * draftConfig so it isn't part of the saved schema or undo timeline.
   */
  canvasScale: number;
  setCanvasScale: (scale: number) => void;

  /**
   * Live "what am I doing right now" readout for the native renderer's
   * editable sections — set during a drag or resize, cleared on
   * gesture-end. The canvas's top bar renders this as a small pill so
   * the user always sees concrete numbers ("Plasticity Chart · x = 50,
   * y = 420 pt") instead of a silent ghost movement.
   *
   * Excluded from the undo timeline (partialize keeps only draftConfig)
   * so a gesture's transient readout doesn't pollute history.
   */
  gestureReadout: GestureReadout | null;
  setGestureReadout: (readout: GestureReadout | null) => void;

  /**
   * Phase 8.3: explicit "render the PDF preview now" trigger. The right-pane
   * PreviewPane subscribes to this counter; when it increments, the pane
   * re-fetches `/preview` once. Replaces the prior auto-refresh-on-every-
   * keystroke behaviour — the editor no longer needs PDF round-trips in
   * the edit loop (the in-browser NativeReportRenderer is the live
   * preview), so we only burn a PDF render when the user explicitly asks
   * for one (Refresh button) or right after a successful save.
   */
  pdfRefreshRequest: number;
  bumpPdfRefreshRequest: () => void;
}

/** Snapshot of an in-progress drag or resize gesture. */
export interface GestureReadout {
  /** What the user is doing — drives the icon + label in the readout pill. */
  kind: 'drag' | 'resize';
  /** Friendly section name for context, e.g. "Plasticity Chart". */
  label: string;
  /** New top-left in PDF points (drag + resize when N/W edges move). */
  x?: number;
  y?: number;
  /** New size in PDF points (resize). */
  w?: number;
  h?: number;
}

export const useReportTemplateStore = create<ReportTemplateState>()(
  temporal(
    (set) => ({
      templateKey: null,
      setTemplateKey: (key) => set({ templateKey: key }),

      draftConfig: null,
      setDraftConfig: (next) => set({ draftConfig: next }),
      resetDraftConfig: (next) => set({ draftConfig: next, publishedConfig: next }),

      publishedConfig: null,
      setPublishedConfig: (next) => set({ publishedConfig: next }),

      previewWorkOrderDetailKey: null,
      setPreviewWorkOrderDetailKey: (key) => set({ previewWorkOrderDetailKey: key }),

      activeSection: null,
      setActiveSection: (section) => set({ activeSection: section }),

      previewPdfBlobUrl: null,
      // Setter owns the URL.revokeObjectURL lifecycle. Whenever a new
      // blob URL is written (or the field is cleared) we revoke the old
      // one so there's no per-render memory leak.
      setPreviewPdfBlobUrl: (url) => set((s) => {
        if (s.previewPdfBlobUrl && s.previewPdfBlobUrl !== url) {
          try { URL.revokeObjectURL(s.previewPdfBlobUrl); } catch { /* no-op */ }
        }
        return { previewPdfBlobUrl: url };
      }),

      previewPdfLoading: false,
      setPreviewPdfLoading: (loading) => set({ previewPdfLoading: loading }),

      previewPdfError: null,
      setPreviewPdfError: (err) => set({ previewPdfError: err }),

      // Default 0.7 matches the pre-zoom canvas scale → no observable
      // change for users who never touch the zoom controls.
      canvasScale: 0.7,
      setCanvasScale: (scale) => set({
        canvasScale: Math.max(0.4, Math.min(2.5, scale)),
      }),

      gestureReadout: null,
      setGestureReadout: (readout) => set({ gestureReadout: readout }),

      pdfRefreshRequest: 0,
      bumpPdfRefreshRequest: () => set((s) => ({ pdfRefreshRequest: s.pdfRefreshRequest + 1 })),
    }),
    {
      // History only tracks the draft config — the rest is UI state that
      // shouldn't be undoable (tab selection, current preview WOD, etc.).
      partialize: (s) => ({ draftConfig: s.draftConfig }) as Pick<ReportTemplateState, 'draftConfig'>,
      // Coalesce rapid edits (e.g. dragging a slider) into a single
      // history entry so undo doesn't have to step through every pixel.
      handleSet: (handleSet) =>
        debounce<typeof handleSet>(handleSet, 300),
      // Keep the last 50 snapshots — plenty for a typical editing session,
      // bounded so memory doesn't grow unbounded across long sessions.
      limit: 50,
    },
  ),
);

/** Convenience hook to access undo/redo from anywhere in the editor. */
export const useReportTemplateTimeline = () =>
  (useReportTemplateStore.temporal as unknown as { getState: () => TemporalState<unknown> }).getState();

/** Reactively-tracked undo/redo counts (for enabling/disabling toolbar buttons). */
export const useReportTemplateHistoryCounts = () => {
  // The temporal store is itself a zustand store, so we subscribe to it.
  const temporalStore = (useReportTemplateStore.temporal as unknown as {
    subscribe: (l: () => void) => () => void;
    getState: () => TemporalState<unknown>;
  });
  // Force re-render on history change by reading state via a useSyncExternalStore-like pattern.
  // Cheaper than pulling react's internal hook directly: since this hook is only used in the
  // toolbar (a single place) we just call getState every render.
  return {
    pastSize: temporalStore.getState().pastStates.length,
    futureSize: temporalStore.getState().futureStates.length,
  };
};

function debounce<T extends (...args: any[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pendingArgs: any[] | null = null;
  const wrapped = ((...args: any[]) => {
    pendingArgs = args;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      if (pendingArgs) fn(...pendingArgs);
      pendingArgs = null;
      timer = null;
    }, ms);
  }) as T;
  return wrapped;
}
